import * as React from 'react'
import { Popover as PopoverPrimitive } from '@base-ui/react/popover'

import { cn } from '@/registry/reapit/lib/utils'
import { asChildProps } from '@/registry/reapit/lib/as-child-props'

// ---------------------------------------------------------------------------
// Popover — migrated from Radix to Base UI
// ---------------------------------------------------------------------------
// Structural changes:
//   Radix: Portal > Content
//   Base UI: Portal > Positioner > Popup
//
// CSS variable rename:
//   --radix-popover-content-transform-origin → --transform-origin
//
// Data attribute changes:
//   data-[state=open/closed] → data-[open] / data-[starting-style] / data-[ending-style]
//   data-[side=*] still available on Positioner
// ---------------------------------------------------------------------------

function Popover({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />
}

function PopoverTrigger({
  asChild,
  children,
  ...props
}: Omit<React.ComponentProps<typeof PopoverPrimitive.Trigger>, 'render'> & {
  asChild?: boolean
}) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...asChildProps(asChild, children)} {...props} />
}

function PopoverContent({
  className,
  align = 'center',
  // DS: floating surfaces use an 8 px gap from their trigger.
  sideOffset = 8,
  side = 'bottom',
  children,
  ...props
}: {
  className?: string
  sideOffset?: number
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  children?: React.ReactNode
} & Omit<React.ComponentProps<typeof PopoverPrimitive.Popup>, 'className' | 'children'>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner side={side} sideOffset={sideOffset} align={align}>
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={cn(
            // DS: bg-popover surface, --border, radius-lg (8 px), shadow-md.
            // Base UI: --transform-origin replaces --radix-popover-content-transform-origin
            // Transition-based animation via data-[starting-style] / data-[ending-style]
            'z-50 w-72 origin-[var(--transform-origin)] rounded-lg border bg-popover p-4 text-popover-foreground shadow-md outline-hidden transition-[opacity,scale] duration-150 scale-100 opacity-100 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
            className,
          )}
          {...props}
        >
          {children}
        </PopoverPrimitive.Popup>
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  )
}

function PopoverAnchor({ ...props }: React.ComponentProps<'div'>) {
  // Base UI Popover does not have a built-in Anchor component like Radix.
  // We render a plain div that can be used as a positioning reference.
  return <div data-slot="popover-anchor" {...props} />
}

function PopoverHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="popover-header" className={cn('flex flex-col gap-1 text-sm', className)} {...props} />
}

function PopoverTitle({ className, ...props }: React.ComponentProps<'h2'>) {
  return <div data-slot="popover-title" className={cn('font-medium', className)} {...props} />
}

function PopoverDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return <p data-slot="popover-description" className={cn('text-muted-foreground', className)} {...props} />
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor, PopoverHeader, PopoverTitle, PopoverDescription }
